import { LitElement, html, nothing, css } from 'lit'
import { customElement } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT } from '../../util/breakpoints'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { QuoteService } from '../../services/quote-service'
import { decryptAndParseJson } from '../../util/crypto'
import { isEmbeddedFlow } from '../../util/embedded'

@customElement('rq-agent-banner')
export class AgentBanner extends LitElement {
  static styles = css`
    .wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }
    .icon {
      color: var(--lh-color-neutral-white, #ffffff);
    }
  `
  @consume({ context: breakpointContext, subscribe: true })
  breakpoint!: BREAKPOINT

  @consume({ context: shopperContext, subscribe: true })
  shopperContextValue?: ShopperContextType

  get isMobile() {
    return (
      this.breakpoint === BREAKPOINT.PHONE ||
      this.breakpoint === BREAKPOINT.TABLET
    )
  }

  render() {
    const retrieveQuoteByEmail =
      this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication
        ?.retrieveQuoteByEmail
    const isRetrievedByEmail = retrieveQuoteByEmail === 'y'

    // Show banner if isAgent OR retrieveQuoteByEmail === 'y' AND referralSource is not PRODUCER_ENTERED (to avoid showing banner for shoppers who manually enter producer info and choose to retrieve by email) AND not in embedded flow (as embedded flow is only for shoppers and we don't want to show banner there even if they choose to retrieve by email)
    if (
      (!this.shopperContextValue?.isAgent &&
        this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication
          ?.referralSource !== 'PRODUCER_ENTERED' &&
        !isRetrievedByEmail) ||
      isEmbeddedFlow()
    )
      return nothing

    // Get producer info from cached forms when retrieved by email
    let session: Record<string, any> | null = null
    if (isRetrievedByEmail) {
      const forms = QuoteService.getCachedAgentForms()
      const pinfo = forms?.producerInfoJ
      if (pinfo) {
        try {
          session = decryptAndParseJson(pinfo)
        } catch {
          // non-blocking
        }
      }
    } else {
      session = this.shopperContextValue?.producerSession || null
    }

    if (!session) return nothing
    const first =
      session.firstName || this.shopperContextValue?.formData?.firstName || ''
    const last =
      session.lastName || this.shopperContextValue?.formData?.lastName || ''
    const displayName =
      [first, last].filter(Boolean).join(' ') || session.fullName || ''
    const initials = (displayName || '')
      .split(' ')
      .map((p: string) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
    const email =
      session.email || this.shopperContextValue?.formData?.email || ''

    // Show "Your Agent:" when retrieved by email, "Logged in as:" when agent is logged in
    const labelText = isRetrievedByEmail ? 'Your Agent:' : 'Logged in as:'

    return html`<lh-container
      padding-top="md"
      padding-bottom="md"
      theme="primary-dark"
      spacing="none"
      gutters=${this.isMobile ? 'sm' : 'md'}
      content-width="xxl"
    >
      <div class="wrapper">
        <span class="icon"><lh-avatar initials=${initials}></lh-avatar></span>
        <lh-typography
          ><strong>${labelText}</strong> ${displayName}${email
            ? ` | ${email}`
            : ''}</lh-typography
        >
      </div>
    </lh-container>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rq-agent-banner': AgentBanner
  }
}
